import React, { createContext, useState, useEffect, useCallback } from "react"
import { translations } from "./buildTranslations"
import type { Locale, Translations } from "./types"

interface LanguageContextType {
  locale: Locale
  t: Translations
  setLocale: (locale: Locale) => void
}

const detectLocale = (): Locale => {
  if (typeof window === "undefined") return "pt"

  const stored = localStorage.getItem("accalia-lang") as Locale | null
  if (stored && (stored === "pt" || stored === "en")) return stored

  const browserLang = navigator.language || ""
  if (browserLang.startsWith("pt")) return "pt"
  return "en"
}

export const LanguageContext = createContext<LanguageContextType>({
  locale: "pt",
  t: translations.pt,
  setLocale: () => {},
})

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [locale, setLocaleState] = useState<Locale>("pt")

  useEffect(() => {
    setLocaleState(detectLocale())
  }, [])

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale)
    if (typeof window !== "undefined") {
      localStorage.setItem("accalia-lang", newLocale)
      document.documentElement.lang = newLocale === "pt" ? "pt-BR" : "en"
    }
  }, [])

  const value: LanguageContextType = {
    locale,
    t: translations[locale],
    setLocale,
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}
